import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { Form } from '@/components/Form'
import { Checkbox } from '@/components/Checkbox'
import { ContentLabel } from '@/components/ContentLabel'
import { Pill } from '@/components/Pill'
import type { Id } from '@/infra/id'
import styles from './AddJobNoteDialog.module.css'

export interface AddJobNoteDialogRecipient {
  userPublicId: Id
  name: string
  role: string
  selected: boolean
}

export interface AddJobNoteDialogProps {
  showing: boolean
  isSending: boolean
  canSend: boolean
  noteInput: ReactNode
  noteCharsRemaining: number
  recipients: AddJobNoteDialogRecipient[]
  onToggleRecipient: (userPublicId: Id, checked: boolean) => unknown
  onSubmit: () => unknown
  onClose: () => unknown
}

// Show remaining chars when approaching the limit.
const COUNTER_THRESHOLD = 200

export function AddJobNoteDialog({
  showing,
  isSending,
  canSend,
  noteInput,
  noteCharsRemaining,
  recipients,
  onToggleRecipient,
  onSubmit,
  onClose,
}: AddJobNoteDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton bordered>
        <ModalSheetTitle>Add Job Note</ModalSheetTitle>
        <ModalSheetHeaderDescription>
          Capture context, updates, or decisions for this job.
        </ModalSheetHeaderDescription>
      </ModalSheetHeader>
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <Form id="addJobNote" onSubmit={onSubmit}>
            {noteInput}
            {noteCharsRemaining <= COUNTER_THRESHOLD && (
              <span className={styles.charCount}>
                {noteCharsRemaining} characters remaining
              </span>
            )}
            {recipients.length > 0 && (
              <div className={styles.notify}>
                <ContentLabel>Notify</ContentLabel>
                {recipients.map((recipient) => (
                  <label
                    key={recipient.userPublicId}
                    className={styles.notifyOption}
                  >
                    <Checkbox
                      checked={recipient.selected}
                      isDisabled={isSending}
                      onChange={(checked) =>
                        onToggleRecipient(recipient.userPublicId, checked)
                      }
                      testId="add-job-note-notify"
                    />
                    <span className={styles.notifyName}>
                      {recipient.name}
                      <Pill shape="square" trim>
                        {recipient.role}
                      </Pill>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            isDisabled={isSending}
            onPress={onClose}
            variant="default"
          />
          <Button
            label="Publish Note"
            type="submit"
            form="addJobNote"
            isPending={isSending}
            isDisabled={!canSend}
            variant="primary"
            testId="add-job-note-confirm"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
